import React, { useState, useEffect } from 'react';
import { throttle } from 'radash';

const hasBackgroundImageUrl = (el) => {
  const style = window.getComputedStyle(el);
  const bg = style.backgroundImage || '';
  return bg.includes('url(');
};

const useCursor = (publishCursorUpdate, whiteboardId) => {
  const [cursorPosition, setCursorPosition] = useState({ x: '', y: '' });

  const updateCursorPosition = (newX, newY) => {
    setCursorPosition({ x: newX, y: newY });
  };

  useEffect(() => {
    if (!cursorPosition || cursorPosition.x === '' || cursorPosition.y === '') {
      return;
    }
    publishCursorUpdate({
      whiteboardId,
      xPercent: cursorPosition?.x,
      yPercent: cursorPosition?.y,
    });
  }, [cursorPosition, publishCursorUpdate, whiteboardId]);

  return [cursorPosition, updateCursorPosition];
};

const useMouseEvents = ({
  whiteboardRef, tlEditorRef, initialZoomRef, isPresenterRef,
}, {
  hasWBAccess,
  whiteboardToolbarAutoHide,
  animations,
  updateCursorPosition,
  toggleToolsAnimations,
  currentPresentationPage,
  zoomChanger,
  setIsMouseDown,
  isInfiniteWhiteboard,
  presentationAreaWidth,
  presentationAreaHeight,
}) => {
  const timeoutIdRef = React.useRef();
  const fingerCountRef = React.useRef(0);
  const initialPinchDistanceRef = React.useRef(0);
  const isPinchingRef = React.useRef(false);

  const PINCH_THRESHOLD = 10;
  const HUNDRED_PERCENT = 100;

  // Add the calculateZoomValue function
  const calculateZoomValue = (localWidth, localHeight) => {
    const calcedZoom = Math.min(
      presentationAreaHeight / localHeight,
      presentationAreaWidth / localWidth,
    );
    return calcedZoom === 0 || calcedZoom === Infinity || Number.isNaN(calcedZoom)
      ? HUNDRED_PERCENT
      : calcedZoom;
  };

  const getDistanceBetweenTouches = (touch1, touch2) => {
    const dx = touch2.clientX - touch1.clientX;
    const dy = touch2.clientY - touch1.clientY;
    return Math.sqrt(dx * dx + dy * dy);
  };

  const handleMouseUp = () => {
    if (timeoutIdRef.current) {
      clearTimeout(timeoutIdRef.current);
    }

    timeoutIdRef.current = setTimeout(() => {
      setIsMouseDown(false);
    }, 1000);

    tlEditorRef?.current?.updateInstanceState({ canMoveCamera: true, isReadonly: false, isToolLocked: true });
  };

  const handleMouseDownWhiteboard = (event) => {
    if (!isPresenterRef.current && !hasWBAccess) {
      const updateProps = { isReadonly: false };

      if (event.button === 1) {
        updateProps.canMoveCamera = false;
      }

      tlEditorRef?.current?.updateInstanceState(updateProps);
    }

    setIsMouseDown(true);
  };

  // const handleMouseDownWindow = (event) => {
  //   const { target } = event;
  //   const editor = tlEditorRef.current;
  //   const presentationInnerWrapper = document.getElementById('presentationInnerWrapper');

  //   if (!(presentationInnerWrapper && presentationInnerWrapper.contains(target))) {
  //     if (editor?.getEditingShape()) {
  //       return editor.complete();
  //     }
  //   }

  //   const selectedShapes = editor?.getSelectedShapes();
  //   if (
  //     selectedShapes?.length === 1
  //     && selectedShapes[0].type === 'frame'
  //     && editor?.getCurrentToolId() === 'select'
  //     && !target.matches('[data-testid*="selection.resize"]')
  //     && !target.matches('[data-testid*="selection.target"]')
  //     && hasBackgroundImageUrl(target)
  //   ) {
  //     editor.selectNone();
  //     return editor.complete();
  //   }

  //   return undefined;
  // };

  const handleMouseDownWindow = (event) => {
    const { target } = event;
    const editor = tlEditorRef.current;
    const presentationInnerWrapper = document.getElementById('presentationInnerWrapper');

    if (!(presentationInnerWrapper && presentationInnerWrapper.contains(target))) {
      if (editor?.getEditingShape()) {
        // Only complete if something is already being edited
        editor.complete();
        return;
      }
    }

    const selectedShapes = editor?.getSelectedShapes();

    if (
      selectedShapes?.length === 1 &&
      selectedShapes[0].type === 'frame' &&
      editor?.getCurrentToolId() === 'select' &&
      !target.matches('[data-testid*="selection.resize"]') &&
      !target.matches('[data-testid*="selection.target"]') &&
      hasBackgroundImageUrl(target)
    ) {
      // Don't force editor.complete() here
      editor.selectNone();
      return;
    }
  };


  const handleMouseEnter = () => {
    if (whiteboardToolbarAutoHide) {
      toggleToolsAnimations(
        'fade-out',
        'fade-in',
        animations ? '.3s' : '0s',
        hasWBAccess || isPresenterRef.current,
      );
    }
  };

  const handleMouseLeave = (event) => {
    if (event.relatedTarget === whiteboardRef.current) {
      return;
    }

    setTimeout(() => {
      updateCursorPosition(-1, -1);
    }, 150);
  };

  // Mouse wheel handling removed - tldraw now handles zoom naturally

  const handleTouchStart = (event) => {
    if (event.touches.length === 2) {
      fingerCountRef.current = 2;
      isPinchingRef.current = false;
      const [t1, t2] = event.touches;
      initialPinchDistanceRef.current = getDistanceBetweenTouches(t1, t2);
    } else if (event.touches.length === 3) {
      fingerCountRef.current = 3;
    } else {
      fingerCountRef.current = 0;
    }
  };

 
  const handleTouchMove = (e) => {
    if (e.touches.length === 2 && initialPinchDistanceRef.current > 0) {
      const currentDistance = getDistanceBetweenTouches(e.touches[0], e.touches[1]);
      const scaleFactor = currentDistance / initialPinchDistanceRef.current;

      // FIX: Convert scale factor to percentage and apply to current zoom
      if (currentPresentationPage && tlEditorRef.current) {
        const baseZoom = calculateZoomValue(
          currentPresentationPage.scaledWidth,
          currentPresentationPage.scaledHeight
        );
        
        const currentZoom = tlEditorRef.current.getCamera().z;
        let currentZoomLevel = currentZoom / baseZoom;
        
        // Apply scale factor to current zoom level
        currentZoomLevel *= scaleFactor;
        
        // Constrain between 100% and 400%
        currentZoomLevel = Math.max(0.5, Math.min(currentZoomLevel, 4));
        
        // Convert to percentage
        const zoomPercentage = Math.round(currentZoomLevel * 100);
        
        // Call zoomChanger with percentage value
        zoomChanger(zoomPercentage);
      }

      // reset base so it's continuous
      initialPinchDistanceRef.current = currentDistance;

      // prevent page scroll
      e.preventDefault();
    }
  };

  const handleTouchEnd = (event) => {
    if (event.touches.length === 0) {
      const count = fingerCountRef.current;

      if (!hasWBAccess) return;

      if (count === 2) {
        if (!isPinchingRef.current) {
          // tlEditorRef.current?.undo();
        }
      } else if (count === 3) {
        // tlEditorRef.current?.redo();
      }
      fingerCountRef.current = 0;
      isPinchingRef.current = false;
      initialPinchDistanceRef.current = 0;
    }
  };

  React.useEffect(() => {
    if (whiteboardToolbarAutoHide) {
      toggleToolsAnimations(
        'fade-in',
        'fade-out',
        animations ? '3s' : '0s',
        hasWBAccess || isPresenterRef.current,
      );
    } else {
      toggleToolsAnimations(
        'fade-out',
        'fade-in',
        animations ? '.3s' : '0s',
        hasWBAccess || isPresenterRef.current,
      );
    }
  }, [whiteboardToolbarAutoHide]);

  React.useEffect(() => {
    const presentationWrapper = document.getElementById('presentationInnerWrapper');
    window.addEventListener('mousedown', handleMouseDownWindow);
    if (presentationWrapper) {
      presentationWrapper.addEventListener('mousedown', handleMouseDownWhiteboard);
      presentationWrapper.addEventListener('mouseup', handleMouseUp);
      presentationWrapper.addEventListener('mouseenter', handleMouseEnter);
      presentationWrapper.addEventListener('mouseleave', handleMouseLeave);
      // Removed mouse wheel listener - let tldraw handle zoom naturally
      presentationWrapper.addEventListener('touchstart', handleTouchStart, { passive: false, capture: true });
      presentationWrapper.addEventListener('touchend', handleTouchEnd, { passive: false, capture: true });
      presentationWrapper.addEventListener('touchmove', handleTouchMove, { passive: false });
    }

    return () => {
      if (presentationWrapper) {
        presentationWrapper.removeEventListener('mousedown', handleMouseDownWhiteboard);
        presentationWrapper.removeEventListener('mouseup', handleMouseUp);
        presentationWrapper.removeEventListener('mouseenter', handleMouseEnter);
        presentationWrapper.removeEventListener('mouseleave', handleMouseLeave);
        // Removed mouse wheel listener cleanup
        presentationWrapper.removeEventListener('touchstart', handleTouchStart);
        presentationWrapper.removeEventListener('touchend', handleTouchEnd);
        presentationWrapper.removeEventListener('touchmove', handleTouchMove);
      }
      window.removeEventListener('mousedown', handleMouseDownWindow);
    };
  }, [
    tlEditorRef,
    isPresenterRef,
    handleMouseDownWhiteboard,
    handleMouseUp,
    handleMouseEnter,
    handleMouseLeave,
    // Removed handleMouseWheel from dependencies
  ]);
};

export {
  useMouseEvents,
  useCursor,
};
